import { Variant } from '@mediashop/app/api/types/ClientProduct';
import classnames from 'classnames';
import Icon from '../../../atom/Icon';
import { useEffect, useMemo, useRef, useState } from 'react';
import VariantOption from './VariantOption';
import getInitialSelectedDimensions from './getInitialSelectedDimensions';
import { FormattedMessage } from 'react-intl';
import { SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import { VariantSelectorProps } from './index';
import uniqBy from 'lodash/uniqBy';
import { DetailedVariant } from '@mediashop/app/api/types/ClientProductDetailed';
import VariantSelectorTitle from './VariantSelectorTitle';

const componentName = 'variant-selector';

const VariantSelector = ({
    isError,
    onVariantChange,
    selectedVariant,
    totalNumberOfVariants,
    variants,
    showSubscriptionVariants = false,
}: VariantSelectorProps) => {
    const optionsRef = useRef<HTMLDivElement | null>(null);
    const variantDimensions = variants[0].attributes.variantDimensions;
    const variantsPerDimension = totalNumberOfVariants / variantDimensions.length;

    const variantCombinations = useMemo<Record<string, string[]>>(() => {
        if (!variantDimensions) {
            return {};
        }
        return variantDimensions.reduce(
            (acc, dimension) => ({
                ...acc,
                [dimension.key]: uniqBy(variants, (variant) => variant.attributes[dimension.key])
                    .sort(
                        (varA, varB) =>
                            ((varA.position - 1) % variantsPerDimension) - ((varB.position - 1) % variantsPerDimension)
                    )
                    .map((variant) => variant.attributes[dimension.key]),
            }),
            {}
        );
    }, [variants, variantDimensions, variantsPerDimension]);

    const availableDimensions = variantDimensions?.length;

    const initialSelectedDimensions = getInitialSelectedDimensions(variantCombinations, variants, selectedVariant);
    const [selectedDimensions, setSelectedDimensions] = useState<Record<string, string>>(initialSelectedDimensions);
    const [lastSelectedDimension, setLastSelectedDimension] = useState(
        variantDimensions ? variantDimensions[0].key : ''
    );

    const onClick = (key: string, value: string, variant?: Variant) => {
        if (variant) {
            const variantAttributes = Object.keys(variant.attributes).reduce(
                (acc, attribute) => ({
                    ...acc,
                    ...(variantCombinations[attribute] && { [attribute]: variant.attributes[attribute] }),
                }),
                {}
            );
            setSelectedDimensions(variantAttributes);
        } else {
            setSelectedDimensions({
                ...selectedDimensions,
                [key]: value,
            });
        }
        setLastSelectedDimension(key);
    };

    const filteredVariants = variants.filter(
        (variant) =>
            (showSubscriptionVariants && (variant as DetailedVariant).attributes.subscriptionBaseVariantSku) ||
            (!showSubscriptionVariants && !(variant as DetailedVariant).attributes.subscriptionBaseVariantSku)
    );

    useEffect(() => {
        if (Object.values(selectedDimensions).every((selectedDimension) => Boolean(selectedDimension))) {
            let selectedVariant = filteredVariants.find((variant) => {
                return Object.entries(selectedDimensions).every(([key, value]) => {
                    return variant.attributes[key] === value;
                });
            });

            if (!selectedVariant) {
                selectedVariant = variants.find((variant) => {
                    return Object.entries(selectedDimensions).every(([key, value]) => {
                        return variant.attributes[key] === value;
                    });
                });
            }

            if (selectedVariant) {
                onVariantChange(selectedVariant);
            }
        }
    }, [selectedDimensions]);

    return (
        <div className={componentName}>
            {Object.keys(variantCombinations).map((dimension, index) => (
                <div
                    key={dimension}
                    className={classnames(
                        `${componentName}__dimension`,
                        `${componentName}__dimension--${availableDimensions > 1 && index === 0 ? 'pill' : 'badge'}`
                    )}
                >
                    <VariantSelectorTitle index={index} variant={selectedVariant} variants={variants} />
                    <div ref={optionsRef} className={`${componentName}__options`}>
                        {variantCombinations[dimension].map((option) => (
                            <VariantOption
                                key={option}
                                selectedVariant={selectedVariant}
                                selectedDimensions={selectedDimensions}
                                variants={variants}
                                variantDimensions={variantDimensions}
                                lastSelectedDimension={lastSelectedDimension}
                                dimension={dimension}
                                type={availableDimensions > 1 && index === 0 ? 'pill' : 'badge'}
                                onClick={onClick}
                                option={option}
                            />
                        ))}
                    </div>

                    {isError ? (
                        <span className={`${componentName}__variant-error`}>
                            <Icon className={`${componentName}__attention-icon`} name="Attention" />
                            <FormattedMessage id="product.emptyVariantError" />
                        </span>
                    ) : (
                        SKIP_RENDER
                    )}
                </div>
            ))}
        </div>
    );
};

export default VariantSelector;
